<template>
  <div>
    <div :class="$style.selector">
      <img
        :src="imageUrl(unit.defId)"
        :alt="unit.defId"
        :title="unit.defId"
        :class="{ [$style.selected]: unit.defId == selectedUnit }"
        width="44"
        height="44"
        v-for="unit in moddableUnits"
        :key="unit.id"
        @click="selectUnit(unit.defId)"
      />
    </div>
    <div :class="$style.toonStats" v-if="this.unit">
      <div :class="$style.toon">
        <h3>{{ selectedUnit }}</h3>
        <div :class="$style.image">
          <div
            :class="[$style[`unitGear${this.unit.gear}`], $style.unitGear]"
          ></div>
          <img
            :src="imageUrl(selectedUnit)"
            :alt="selectedUnit"
            width="300"
            height="300"
          />
        </div>
        <div :class="$style.stars">
          <img
            v-for="n in 7"
            :src="`https://swgoh.gg/static/img/star${
              this.unit.rarity < n ? '-inactive' : ''
            }.png`"
            :key="`star-${n}`"
            :alt="`${this.unit.rarity} stars`"
          />
        </div>
        <div
          :class="{
            [$style.gear]: true,
            [$style[`gear${this.unit.gear}`]]: true,
          }"
        >
          {{ gearLevel }}
        </div>
        <div :class="$style.zetas">{{ zetas }}</div>
      </div>
      <table>
        <tr :class="$style.separator">
          <td>General</td>
          <td>Current</td>
          <td>New</td>
          <td>Change</td>
        </tr>
        <tr
          v-for="stat in generalStats"
          :key="stat"
          :class="$style[changeQuality(stat)]"
          @click="$emit('optimize', stat)"
        >
          <td>{{ shorten(stat) }}</td>
          <td>
            {{ getStat(stat) }}
            <span :class="$style.modStat">{{ getModStat(stat) }}</span>
          </td>
          <td>
            {{ getNewStat(stat) }}
            <span :class="$style.modStat">{{ getNewModStat(stat) }}</span>
          </td>
          <td>{{ getStatChange(stat) }}</td>
        </tr>
        <tr :class="$style.separator">
          <td>Physical</td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
        <tr
          v-for="stat in physicalStats"
          :key="stat"
          :class="$style[changeQuality(stat)]"
          @click="$emit('optimize', stat)"
        >
          <td>{{ shorten(stat) }}</td>
          <td>
            {{ getStat(stat) }}
            <span :class="$style.modStat">{{ getModStat(stat) }}</span>
          </td>
          <td>
            {{ getNewStat(stat) }}
            <span :class="$style.modStat">{{ getNewModStat(stat) }}</span>
          </td>
          <td>{{ getStatChange(stat) }}</td>
        </tr>
        <tr :class="$style.separator">
          <td>Special</td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
        <tr
          v-for="stat in specialStats"
          :key="stat"
          :class="$style[changeQuality(stat)]"
          @click="$emit('optimize', stat)"
        >
          <td>{{ shorten(stat) }}</td>
          <td>
            {{ getStat(stat) }}
            <span :class="$style.modStat">{{ getModStat(stat) }}</span>
          </td>
          <td>
            {{ getNewStat(stat) }}
            <span :class="$style.modStat">{{ getNewModStat(stat) }}</span>
          </td>
          <td>{{ getStatChange(stat) }}</td>
        </tr>
      </table>
      <div :class="$style.mods">
        <Mod
          v-for="slot in 6"
          :key="`slot-${slot}`"
          :mod="modSlot(slot - 1)"
          :hoverable="false"
          :compact="true"
          :class="{ [$style.reassigned]: isReassigned(slot - 1) }"
          @click="$emit('selectedSlot', slot - 1)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import gameData from "/@/gameData.json";
import statNames from "/@/components/stat_names.json";
import statCalculator from "swgoh-stat-calc";
import Mod from "/@/components/Mod.vue";
import { transformPlayer, imageUrlForCharacter } from "/@/utils";

export default {
  props: ["units"],
  components: { Mod },
  data() {
    return {
      selectedUnit: "",
      generalStats: [
        "Health",
        "Protection",
        "Speed",
        "Critical Damage",
        "Potency",
        "Tenacity",
        "Health Steal",
        "Defense Penetration",
      ],
      physicalStats: [
        "Physical Damage",
        "Physical Critical Chance",
        "Armor",
        "Physical Critical Avoidance",
      ],
      specialStats: [
        "Special Damage",
        "Special Critical Chance",
        "Resistance",
        "Special Critical Avoidance",
      ],
      reassignedMods: [],
    };
  },
  created() {
    statCalculator.setGameData(gameData);
  },
  computed: {
    originalUnit() {
      return this.units.find(({ defId }) => defId == this.selectedUnit);
    },
    unit() {
      const unit = this.originalUnit;
      if (!unit) {
        return unit;
      }
      const replacedSlots = this.reassignedMods.map(({ slot }) => slot);
      const unitMods = [
        ...unit.mods.filter(({ slot }) => !replacedSlots.includes(slot)),
        ...this.reassignedMods,
      ];
      return { ...unit, mods: unitMods };
    },
    mods() {
      if (!this.unit) {
        return [];
      }
      return [...transformPlayer({ roster: [this.unit] })].sort(
        (a, b) => a.slot - b.slot
      );
    },
    originalStats() {
      if (!this.originalUnit) {
        return {};
      }
      const stats = statCalculator.calcCharStats(this.originalUnit, {
        gameStyle: true,
        language: statNames,
      });
      return stats;
    },
    stats() {
      if (!this.unit) {
        return {};
      }
      const stats = statCalculator.calcCharStats(this.unit, {
        gameStyle: true,
        language: statNames,
      });
      return stats;
    },
    moddableUnits() {
      if (!this.units) {
        return [];
      }
      return this.units
        .filter(({ combatType, level }) => combatType == 1 && level >= 50)
        .sort((a, b) => {
          if (a.defId < b.defId) {
            return -1;
          }
          if (a.defId > b.defId) {
            return 1;
          }
          return 0;
        });
    },
    gearLevel() {
      return this.unit.relic.currentTier > 2
        ? `R${this.unit.relic.currentTier - 2}`
        : `G${this.unit.gear}`;
    },
    zetas() {
      const [appliedZetas, totalZetas] = this.unit.skills.reduce(
        ([appliedZetas, totalZetas], { isZeta, tier, tiers }) => {
          if (!isZeta) {
            return [appliedZetas, totalZetas];
          }
          if (tier == tiers) {
            return [appliedZetas + 1, totalZetas + 1];
          }
          return [appliedZetas, totalZetas + 1];
        },
        [0, 0]
      );
      if (totalZetas == 0) {
        return "Character has no zeta abilities";
      }
      return `${appliedZetas}/${totalZetas} zetas`;
    },
  },
  methods: {
    getStat(statName) {
      if (!this.originalStats.final) {
        return 0;
      }
      const finalStat = this.formatStat(this.originalStats.final[statName]);

      return `${finalStat}${this.statUnit(finalStat)}`;
    },
    getNewStat(statName) {
      if (!this.stats.final) {
        return 0;
      }
      const finalStat = this.formatStat(this.stats.final[statName]);

      return `${finalStat}${this.statUnit(finalStat)}`;
    },
    getModStat(statName) {
      if (!this.originalStats.mods) {
        return "";
      }
      return this.formatModStat(this.originalStats.mods[statName]);
    },
    getNewModStat(statName) {
      if (!this.stats.mods) {
        return "";
      }
      return this.formatModStat(this.stats.mods[statName]);
    },
    getStatChange(statName) {
      if (!this.originalStats.final || !this.stats.final) {
        return "+0";
      }
      return this.calculateStatChange(this.originalStats, this.stats, statName);
    },
    calculateStatChange(original, current, statName) {
      const originalValue = original.final[statName] || 0;
      const finalValue = current.final[statName] || 0;
      let change = finalValue - originalValue;
      if (!Number.isInteger(change)) {
        change = (change * 100).toFixed(2);
      }

      return `${change >= 0 ? "+" + change : change}`;
    },
    formatModStat(stat) {
      if (!stat) {
        return "";
      }
      return ` (${this.formatStat(stat)})`;
    },
    formatStat(stat) {
      if (!stat) {
        return 0;
      }
      if (Number.isInteger(stat)) {
        return stat;
      }
      return (stat * 100).toFixed(2);
    },
    changeQuality(statName) {
      const change = this.getStatChange(statName);
      if (change > 0) {
        return "positive";
      }
      if (change < 0) {
        return "negative";
      }
      return "";
    },
    statUnit(stat) {
      if (Number.isInteger(stat)) {
        return "";
      }
      return "%";
    },
    assignMod(mod) {
      if (!this.unit) {
        return;
      }
      this.reassignedMods = [
        ...this.reassignedMods.filter(({ slot }) => slot != mod.slot + 1),
        { ...mod, slot: mod.slot + 1 },
      ];
    },
    selectUnit(defId) {
      this.selectedUnit = this.selectedUnit == defId ? "" : defId;
      this.reassignedMods = [];
      this.$emit("assign", this.calculateModChange());
    },
    modSlot(displaySlot) {
      return (
        this.mods.find(({ slot }) => slot == displaySlot) || {
          slot: displaySlot,
        }
      );
    },
    shorten(statName) {
      return statName
        .replace("Physical", "P.")
        .replace("Special", "S.")
        .replace("Defense", "Def.")
        .replace("Critical", "C.");
    },
    isReassigned(targetSlot) {
      return this.reassignedMods.some(({ slot }) => slot - 1 == targetSlot);
    },
    calculateModChange() {
      const specialStats = {
        Offense: ["Physical Damage", "Special Damage"],
        Defense: ["Armor", "Resistance"],
        "Critical Chance": ["Physical Critical Chance"],
        "Critical Avoidance": ["Physical Critical Avoidance"],
      };
      return (mod) => {
        const changedMods = [
          ...this.unit.mods.filter(({ slot }) => slot != mod.slot + 1),
          { ...mod, slot: mod.slot + 1 },
        ];
        const newStats = statCalculator.calcCharStats(
          { ...this.unit, mods: changedMods },
          {
            gameStyle: true,
            language: statNames,
          }
        );
        const calcChange = (statName) => {
          if (specialStats[statName]) {
            return specialStats[statName]
              .map((toonStatName) =>
                this.calculateStatChange(this.stats, newStats, toonStatName)
              )
              .join("/");
          }
          return this.calculateStatChange(this.stats, newStats, statName);
        };
        const changes = mod.secondaryStat.reduce(
          (changes, { name: statName }) => ({
            ...changes,
            [statName]: calcChange(statName),
          }),
          {}
        );
        changes[mod.primaryStat.name] = calcChange(mod.primaryStat.name);
        return changes;
      };
    },
    imageUrl(character) {
      return imageUrlForCharacter(character);
    },
  },
};
</script>

<style lang="scss" module>
.toon-stats {
  background-color: #3d546df0;
  border: 3px solid aquamarine;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;

  img {
    flex: 0 0 300px;
  }

  table {
    border-spacing: 0;
    .separator,
    .separator:nth-child(odd) {
      background-color: azure;
      color: #3d546df0;
      font-size: larger;
      font-weight: bold;
      > td:first-child {
        text-align: center;
      }
    }
    tr > td:first-child {
      text-align: right;
    }
    tr:nth-child(odd) {
      background-color: #ffffff30;
    }

    tr:not(.separator):hover {
      outline: solid;
      cursor: pointer;
    }

    .positive {
      background-color: #{scale-color(green, $lightness: 30%, $alpha: -70%)};
    }

    tr:nth-child(odd).positive {
      background-color: #{scale-color(green, $lightness: 70%, $alpha: -70%)};
    }

    .negative {
      background-color: #{scale-color(
          saddlebrown,
          $lightness: 30%,
          $alpha: -70%
        )};
    }

    tr:nth-child(odd).negative {
      background-color: #{scale-color(
          saddlebrown,
          $lightness: 70%,
          $alpha: -70%
        )};
    }
  }

  .mods {
    display: flex;
    flex-wrap: wrap;
    max-width: 600px;
    > div {
      flex: 1 0 45%;
    }

    > .reassigned {
      border: 1px dashed azure;
    }
  }

  .mod-stat {
    display: none;
  }

  @media (min-width: 650px) {
    .mod-stat {
      display: inline;
    }
  }
}

.selector {
  img {
    border: 4px solid #ccc;
    border-radius: 50%;
  }
  .selected {
    border-color: aquamarine;
  }
}

.toon {
  h3 {
    background-color: azure;
    color: #3d546df0;
    margin: 0;
    padding: 0.1rem;
  }
}

.image {
  position: relative;

  img {
    border-radius: 50%;
  }
}

.unit-gear {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-repeat: no-repeat;
}

.unit-gear-13 {
  background-image: url(https://swgoh.gg/static/img/ui/g13-frame-atlas.png);
  background-size: 125%;
  background-position: center;
  background-position-y: 97.3%;
}

.unit-gear-12 {
  background-image: url(https://swgoh.gg/static/img/ui/gear-icon-g12.svg);
}

.unit-gear-11 {
  background-image: url(https://swgoh.gg/static/img/ui/gear-icon-g11.svg);
}

.unit-gear-10 {
  background-image: url(https://swgoh.gg/static/img/ui/gear-icon-g10.svg);
}

.unit-gear-9 {
  background-image: url(https://swgoh.gg/static/img/ui/gear-icon-g9.svg);
}

.unit-gear-8 {
  background-image: url(https://swgoh.gg/static/img/ui/gear-icon-g8.svg);
}

.unit-gear-7 {
  background-image: url(https://swgoh.gg/static/img/ui/gear-icon-g7.svg);
}

.unit-gear-6 {
  background-image: url(https://swgoh.gg/static/img/ui/gear-icon-g6.svg);
}

.unit-gear-5 {
  background-image: url(https://swgoh.gg/static/img/ui/gear-icon-g5.svg);
}

.unit-gear-4 {
  background-image: url(https://swgoh.gg/static/img/ui/gear-icon-g4.svg);
}

.unit-gear-3 {
  background-image: url(https://swgoh.gg/static/img/ui/gear-icon-g3.svg);
}

.unit-gear-2 {
  background-image: url(https://swgoh.gg/static/img/ui/gear-icon-g2.svg);
}

.unit-gear-1 {
  background-image: url(https://swgoh.gg/static/img/ui/gear-icon-g1.svg);
}

.stars,
.gear,
.zetas {
  font-weight: bold;
  font-size: larger;
}

.gear-13 {
  background-color: #ccccccb0;
  text-shadow: #fff 1px 0 10px;
}
.gear-12 {
  background-color: #ffd036;
}

.gear-11,
.gear-10,
.gear-9,
.gear-8,
.gear-7 {
  background-color: #9040fc;
}
.gear-6,
.gear-5,
.gear-4 {
  background-color: #05bcfb;
}
.gear-3,
.gear-2 {
  background-color: #98fd33;
}
</style>
